import Decimal from "decimal.js-light";

import {
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_FEET_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_FEET_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
} from "@/utils/constants";

import {
  sumTotalAreaOfSquareFeetInSquareFeet,
  sumTotalAreaOfSquareFeetInSquareMeters,
  sumTotalAreaOfSquareMetersInSquareMeters,
  sumTotalCustomFormulaQuantity,
  sumTotalLinealFeetInMm,
  sumTotalLinealMetersLengthInMetres,
  sumTotalLinealMetersLengthInMm,
} from "./measurement-aggregate";
import { convertMmToFeet } from "./measurement-conversion";
/**
 * Returns quantity for Basic Quantities strategy (unitless count).
 *
 * @param {Object} params
 * @param {number} params.quantity - Item quantity (or actualQuantity for actual cost)
 * @returns {Decimal} Quantity for price/cost computation
 */
export const getBasicQuantity = ({ quantity }) => {
  return Decimal(quantity ?? 0);
};

/**
 * Returns quantity for Custom Formula strategy (quantity × formula output).
 *
 * @param {Object} params
 * @param {number} params.quantity - Item quantity (or actualQuantity for actual cost)
 * @param {number} params.formulaOutput - Item customFormulaOutput (or actualCustomFormulaOutput)
 * @returns {Decimal} Quantity for price/cost computation (4 decimal places)
 */
export const getCustomFormulaQuantity = ({ quantity, formulaOutput }) => {
  return sumTotalCustomFormulaQuantity({ quantity, formulaOutput });
};

/**
 * Calculates lineal metres quantity from items with amount and length.
 * Uses minLength when item length is below threshold for pricing.
 * Delegates to measurement.getTotalLinealLengthInMetres.
 *
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number}>} params.items - Array of dimension items
 * @param {number} params.minLengthInMm - Minimum length in mm for pricing
 * @returns {{ total: Decimal, forPrice: Decimal }} Total and forPrice in lineal metres (4 decimal places)
 */
export const getLinealMetresQuantityInMetres = ({ items, minLengthInMm }) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0), forPrice: Decimal(0) };
  }
  const { total, forPrice } = sumTotalLinealMetersLengthInMetres({
    items,
    minLengthInMm,
  });

  return {
    total: Decimal(total),
    forPrice: Decimal(forPrice),
  };
};

/**
 * Calculates lineal metres quantity from items with amount and length.
 * Uses minLength when item length is below threshold for pricing.
 * Delegates to measurement.getTotalLinealLengthInMm.
 *
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number}>} params.items - Array of dimension items
 * @param {number} params.minLengthProducts - Minimum length in mm for pricing
 * @returns {{ total: Decimal, forPrice: Decimal }} Total and forPrice in lineal metres (4 decimal places)
 */
export const getLinealMetresQuantityInMm = ({ items, minLengthInMm }) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0), forPrice: Decimal(0) };
  }
  const { total, forPrice } = sumTotalLinealMetersLengthInMm({
    items,
    minLengthInMm,
  });

  return {
    total: Decimal(total),
    forPrice: Decimal(forPrice),
  };
};

/**
 * Calculates square metres quantity from items with length and width.
 * Delegates to measurement.getTotalAreaInSquareMetresFromItems.
 *
 * @param {Object} params
 * @param {Array<{amount: number, length?: number, width?: number}>} [params.items=[]] - Array of dimension items
 * @returns {{ total: Decimal }} Total area in square metres (4 decimal places)
 */
export const getSquareMetresQuantityInSquareMetres = ({ items = [] } = {}) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0) };
  }
  const { total } = sumTotalAreaOfSquareMetersInSquareMeters({ items: arr });
  return {
    total: Decimal(total),
  };
};

/**
 * Calculates lineal feet quantity from items using minLength for pricing.
 *
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number, lengthMm?: number}>} params.items - Array of dimension items
 * @param {number} [params.minLengthInMm=0] - Minimum length in mm for pricing
 * @returns {{ total: Decimal, forPrice: Decimal }} Total and forPrice in lineal feet (4 decimal places)
 */
export const getLinealFeetQuantityInMm = ({ items, minLengthInMm = 0 }) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0), forPrice: Decimal(0) };
  }
  const { total: totalMm, forPrice: forPriceMm } = sumTotalLinealFeetInMm({
    items: arr,
    minLengthInMm,
  });
  return {
    total: totalMm,
    forPrice: forPriceMm,
  };
};

/**
 * Calculates lineal feet quantity from items using minLength for pricing.
 *
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number, lengthMm?: number}>} params.items - Array of dimension items
 * @param {number} [params.minLengthInMm=0] - Minimum length in mm for pricing
 * @returns {{ total: Decimal, forPrice: Decimal }} Total and forPrice in lineal feet (4 decimal places)
 */
export const getLinealFeetQuantityInFeet = ({ items, minLengthInMm = 0 }) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0), forPrice: Decimal(0) };
  }
  const { total: totalMm, forPrice: forPriceMm } = sumTotalLinealFeetInMm({
    items: arr,
    minLengthInMm,
  });
  return {
    total: Decimal(convertMmToFeet(totalMm)),
    forPrice: Decimal(convertMmToFeet(forPriceMm)),
  };
};

/**
 * Calculates square feet quantity from items with totalAreaInSquareFeet.
 *
 * @param {Object} params
 * @param {Array<{totalAreaInSquareFeet?: number}>} [params.items=[]] - Array of dimension items
 * @returns {{ total: Decimal }} Total area in square feet (4 decimal places)
 */
export const getSquareFeetQuantityInSquareFeet = ({ items = [] } = {}) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0) };
  }
  const { total } = sumTotalAreaOfSquareFeetInSquareFeet({ items: arr });
  return {
    total: Decimal(total),
  };
};

/**
 * Calculates square feet quantity from items with totalAreaInSquareMeter.
 *
 * @param {Object} params
 * @param {Array<{totalAreaInSquareFeet?: number}>} [params.items=[]] - Array of dimension items
 * @returns {{ total: Decimal }} Total area in square feet (6 decimal places)
 */
export const getSquareFeetQuantityInSquareMeter = ({ items = [] } = {}) => {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) {
    return { total: Decimal(0) };
  }
  const { total } = sumTotalAreaOfSquareFeetInSquareMeters({ items: arr });
  return {
    total: Decimal(total),
  };
};

/**
 * use this when putting values in the quantity
 * Returns the quantity used for price/cost computation based on the item's pricing strategy.
 * Dispatches to strategy-specific helpers (lineal metres/feet, square metres/feet, custom formula, basic quantity).
 *
 * @param {Object} params
 * @param {number} [params.quantity=1] - Item quantity (used for basic quantities and custom formula)
 * @param {Array} [params.lengthItems] - Dimension items (subitems or lengthItems): amount/length for lineal, length/width or totalArea* for square
 * @param {string} [params.pricingStrategy] - One of PRICING_STRATEGY_* constants
 * @param {number} [params.minLengthForLinealMeters=0] - Minimum length in mm for lineal metres pricing
 * @param {number} [params.minLengthForLinealFeet=0] - Minimum length in mm for lineal feet pricing
 * @param {number} [params.formulaOutput=0] - Custom formula output (for custom formula strategy)
 * @returns {Decimal|undefined} Quantity for price/cost computation, or undefined if strategy returns no value
 */
export const getQuantityByPricingStrategy = ({
  quantity = 1,
  lengthItems,
  pricingStrategy,
  minLengthForLinealMeters = 0,
  minLengthForLinealFeet = 0,
  formulaOutput = 0,
}) => {
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
    return getLinealMetresQuantityInMetres({
      items: lengthItems,
      minLengthInMm: minLengthForLinealMeters,
    })?.forPrice;
  }
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
    return getLinealFeetQuantityInFeet({
      items: lengthItems,
      minLengthInMm: minLengthForLinealFeet,
    })?.forPrice;
  }
  if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
    return getSquareFeetQuantityInSquareFeet({ items: lengthItems })?.total;
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
    return getSquareMetresQuantityInSquareMetres({ items: lengthItems })?.total;
  }

  if (pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
    return Decimal(quantity);
  }

  if (pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
    return getCustomFormulaQuantity({ quantity, formulaOutput });
  }

  return Decimal(quantity);
};

/**
 * use this when putting the value in the sumLengths
 * Returns the sum of lengths/areas by pricing strategy (for display or aggregation).
 * Lineal strategies return totals in mm; square metres in m²; square feet in m².
 *
 * @param {Object} params
 * @param {Array} [params.lengthItems=[]] - Dimension items (amount/length, length/width, or totalArea*)
 * @param {string} [params.pricingStrategy] - One of PRICING_STRATEGY_LINEAL_METRES_LABEL, SQUARE_METRES_LABEL, LINEAL_FEET_LABEL, SQUARE_FEET_LABEL
 * @param {number} [params.minLength=0] - Minimum length in mm (used for lineal strategies)
 * @returns {Decimal|{ total: Decimal, forPrice?: Decimal }} Sum in strategy-specific units, or Decimal(0) if strategy not matched
 */
export const getSumLengthsByPricingStrategy = ({
  lengthItems = [],
  pricingStrategy,
  minLength = 0,
}) => {
  const items = Array.isArray(lengthItems) ? lengthItems : [];
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
    return sumTotalLinealMetersLengthInMm({ items, minLengthInMm: minLength });
  }
  if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
    return sumTotalAreaOfSquareMetersInSquareMeters({ items });
  }
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
    return sumTotalLinealFeetInMm({ items, minLengthInMm: minLength });
  }
  if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
    return sumTotalAreaOfSquareFeetInSquareMeters({ items });
  }
  return { total: Decimal(0), forPrice: Decimal(0) };
};
